import { useState } from "react";
import { z } from "zod";
import { fcfa, useApi } from "../../api/hooks";
import { EtatChargement, EtatErreur } from "../../composants/Avis";
import { ApercuDocument } from "../../composants/ApercuDocument";
import { Tableau, Tuiles } from "../../composants/Tableau";
import { DocumentSchema, ListeDocumentsSchema, type Document } from "../../api/schemas";
import type { EcranProps } from "../types";

// ===========================================================================
// DOSSIER ET TRAITEMENT — identité, banques, imports
// ===========================================================================

const DossierSchema = z.object({
  id: z.string(),
  raisonSociale: z.string(),
  contact: z.string().nullable(),
  telephone: z.string().nullable(),
  email: z.string().nullable(),
  actif: z.boolean(),
  regime: z.string(),
  modeTenue: z.string(),
  modules: z.array(z.string()),
  iaAutorisee: z.boolean(),
  estDemonstration: z.boolean(),
  identite: z
    .object({
      rccm: z.string().nullable(),
      ncc: z.string().nullable(),
      centreImpots: z.string().nullable(),
      dateDebut: z.string().nullable(),
      formeJuridique: z.string().nullable(),
      capitalSocial: z.number().nullable(),
      localisation: z.string().nullable(),
      associes: z.string().nullable(),
      gerants: z.string().nullable(),
      obligations: z.array(z.string()),
    })
    .nullable(),
  banques: z.array(
    z.object({
      id: z.string(),
      banque: z.string(),
      numero: z.string(),
      devise: z.string(),
      compteComptable: z.string(),
      principal: z.boolean(),
      actif: z.boolean(),
    }),
  ),
});

export function DossierTraitement({ dossierId }: EcranProps) {
  const q = useApi(["dossier", dossierId], `/dossiers/${dossierId}`, DossierSchema);

  if (q.isLoading) return <EtatChargement quoi="du dossier" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data) return null;

  const d = q.data;

  return (
    <div>
      <section>
        <h2>Identité de l'entreprise</h2>
        <dl className="identite">
          <div><dt>Raison sociale</dt><dd>{d.raisonSociale}</dd></div>
          <div><dt>Régime</dt><dd>{d.regime}</dd></div>
          <div><dt>Mode de tenue</dt><dd>{d.modeTenue}</dd></div>
          <div><dt>RCCM</dt><dd>{d.identite?.rccm ?? "—"}</dd></div>
          <div><dt>NCC</dt><dd>{d.identite?.ncc ?? "—"}</dd></div>
          <div><dt>Centre des impôts</dt><dd>{d.identite?.centreImpots ?? "—"}</dd></div>
          <div><dt>Forme juridique</dt><dd>{d.identite?.formeJuridique ?? "—"}</dd></div>
          <div>
            <dt>Capital social</dt>
            <dd>{d.identite?.capitalSocial === null || d.identite?.capitalSocial === undefined ? "—" : fcfa(d.identite.capitalSocial)}</dd>
          </div>
          <div><dt>Début d'activité</dt><dd>{d.identite?.dateDebut ?? "—"}</dd></div>
          <div><dt>Localisation</dt><dd>{d.identite?.localisation ?? "—"}</dd></div>
          <div><dt>Gérants</dt><dd>{d.identite?.gerants ?? "—"}</dd></div>
          <div><dt>Associés</dt><dd>{d.identite?.associes ?? "—"}</dd></div>
        </dl>
      </section>

      <section>
        <h2>Obligations retenues à la DFE</h2>
        <p className="resume">
          Le régime donne les natures <em>possibles</em>. La{" "}
          <strong>déclaration fiscale d'existence dit lesquelles ont été retenues</strong> —
          et elle en porte parfois que le régime seul ne laisserait pas deviner.
        </p>
        {!d.identite?.obligations.length ? (
          <p className="alerte">
            La DFE n'est pas renseignée. Les natures suivies sont celles que le
            régime rend possibles, pas celles qui ont été retenues.
          </p>
        ) : (
          <ul className="obligations">
            {d.identite.obligations.map((o) => (
              <li key={o}><code>{o}</code></li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2>Comptes bancaires</h2>
        <p className="resume">
          Chaque banque écrit sur <strong>son propre compte comptable</strong>.
          Le compte fourni n'est jamais le seul.
        </p>
        <Tableau
          colonnes={[
            { cle: "b", titre: "Banque", rendu: (b) => b.banque },
            { cle: "n", titre: "Numéro", rendu: (b) => b.numero },
            { cle: "d", titre: "Devise", rendu: (b) => b.devise },
            { cle: "c", titre: "Compte comptable", rendu: (b) => <code>{b.compteComptable}</code> },
            { cle: "p", titre: "Type", rendu: (b) => (b.principal ? "principal" : "secondaire") },
          ]}
          lignes={d.banques}
          cle={(b) => b.id}
          vide="Aucun compte bancaire déclaré."
        />
      </section>

      <section>
        <h2>Traitement par l'intelligence artificielle</h2>
        <p className={d.iaAutorisee ? "alerte" : "resume"}>
          {d.iaAutorisee
            ? "Le traitement IA est AUTORISÉ sur ce dossier."
            : "Le traitement IA est interdit sur ce dossier (défaut)."}{" "}
          Aucune donnée client ne part vers un modèle sans décision explicite du
          gérant — secret professionnel.
        </p>
      </section>
    </div>
  );
}

// ===========================================================================
// CONSULTATION CLIENT — arbre documentaire
// ===========================================================================

export function ConsultationClient({ dossierId }: EcranProps) {
  const [apercu, setApercu] = useState<Document | null>(null);
  const [recherche, setRecherche] = useState("");

  const q = useApi(
    ["documents", dossierId],
    `/dossiers/${dossierId}/documents`,
    ListeDocumentsSchema,
  );

  if (q.isLoading) return <EtatChargement quoi="des documents" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  const docs = (q.data ?? []).filter((d) =>
    recherche === "" ? true : d.cheminLogique.toLowerCase().includes(recherche.toLowerCase()),
  );

  const scelles = docs.filter((d) => d.scelle).length;
  const alteres = docs.filter((d) => d.altere).length;

  return (
    <div>
      <Tuiles
        tuiles={[
          { titre: "Documents", valeur: docs.length },
          { titre: "Scellés", valeur: scelles },
          { titre: "Altérations détectées", valeur: alteres, alerte: alteres > 0 },
          {
            titre: "Volume",
            valeur: `${Math.round(docs.reduce((s, d) => s + d.taille, 0) / 1024 / 1024)} Mo`,
          },
        ]}
      />

      <label className="filtre">
        Rechercher
        <input
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder="nom de fichier ou dossier"
        />
      </label>

      <Tableau
        colonnes={[
          { cle: "c", titre: "Emplacement", rendu: (d) => d.cheminLogique },
          { cle: "t", titre: "Type", rendu: (d) => d.contentType },
          {
            cle: "s",
            titre: "Taille",
            montant: true,
            rendu: (d) => `${Math.round(d.taille / 1024)} Ko`,
          },
          {
            cle: "e",
            titre: "Empreinte",
            rendu: (d) => <code title={d.sha256}>{d.sha256.slice(0, 12)}</code>,
          },
          {
            cle: "st",
            titre: "État",
            rendu: (d) => (
              <>
                {d.scelle && <span className="jeton jeton--scelle">Scellé</span>}
                {d.altere && <span className="jeton jeton--alerte">Altéré</span>}
                {!d.scelle && !d.altere && "—"}
              </>
            ),
          },
          { cle: "dep", titre: "Déposé par", rendu: (d) => d.deposePar ?? "—" },
        ]}
        lignes={docs}
        cle={(d) => d.id}
        surLigne={setApercu}
        vide="Aucun document déposé."
      />

      {apercu && (
        <ApercuDocument
          dossierId={dossierId}
          document={apercu}
          onFermer={() => setApercu(null)}
        />
      )}
    </div>
  );
}

// ===========================================================================
// JUSTIFICATIFS DU JOURNAL
// ===========================================================================

const LigneJournalSchema = z.object({
  id: z.string(),
  date: z.string(),
  numeroPiece: z.string().nullable(),
  libelle: z.string(),
  nature: z.string(),
  tiers: z.string().nullable(),
  sens: z.string(),
  montant: z.number(),
  pieceScannee: z.string(),
  pieceRangee: z.boolean(),
  dossierDuJustificatif: z.string().nullable(),
  justificatifId: z.string().nullable(),
  validePar: z.string().nullable(),
  montantConfirme: z.boolean(),
  montantInvraisemblable: z.boolean(),
});

export function Justificatifs({ dossierId, annee, mois }: EcranProps) {
  const journal = useApi(
    ["journal", dossierId, annee, mois],
    `/dossiers/${dossierId}/journal?annee=${annee}&mois=${mois}`,
    z.array(LigneJournalSchema),
  );

  const documents = useApi(
    ["documents", dossierId],
    `/dossiers/${dossierId}/documents`,
    ListeDocumentsSchema,
  );

  if (journal.isLoading || documents.isLoading) return <EtatChargement quoi="des justificatifs" />;
  if (journal.error) return <EtatErreur erreur={journal.error} />;

  const lignes = journal.data ?? [];
  const docs = documents.data ?? [];

  const rangees = lignes.filter((l) => l.pieceRangee);
  const introuvables = lignes.filter((l) => l.justificatifId !== null && !l.pieceRangee);
  const absentes = lignes.filter((l) => l.justificatifId === null);

  // Les fichiers déposés qu'AUCUNE écriture ne réclame.
  const reclames = new Set(lignes.map((l) => l.justificatifId).filter(Boolean));
  const orphelins = docs.filter((d) => !reclames.has(d.id));

  return (
    <div>
      <Tuiles
        tuiles={[
          { titre: "Rangées", valeur: rangees.length },
          { titre: "Introuvables", valeur: introuvables.length, alerte: introuvables.length > 0 },
          { titre: "Absentes", valeur: absentes.length, alerte: absentes.length > 0 },
          {
            titre: "Montant sans pièce",
            valeur: fcfa(absentes.reduce((s, l) => s + l.montant, 0)),
            alerte: absentes.length > 0,
          },
          { titre: "Pièces non réclamées", valeur: orphelins.length },
        ]}
      />

      <section>
        <h2>Écritures sans justificatif</h2>
        <p className="resume">
          Les colonnes « scanné » et « rangé » sont un <strong>constat</strong>,
          jamais une déclaration : « rangé » est vérifié sur le stockage.
        </p>
        <Tableau
          colonnes={[
            { cle: "d", titre: "Date", rendu: (l) => l.date },
            { cle: "p", titre: "Pièce", rendu: (l) => l.numeroPiece ?? "—" },
            { cle: "l", titre: "Libellé", rendu: (l) => l.libelle },
            { cle: "n", titre: "Nature", rendu: (l) => l.nature },
            { cle: "m", titre: "Montant", montant: true, rendu: (l) => fcfa(l.montant) },
            { cle: "s", titre: "Scanné", rendu: (l) => l.pieceScannee },
          ]}
          lignes={absentes}
          cle={(l) => l.id}
          vide="Toutes les écritures portent un justificatif."
        />
      </section>

      <section>
        <h2>Écritures dont le justificatif est introuvable</h2>
        <Tableau
          colonnes={[
            { cle: "d", titre: "Date", rendu: (l) => l.date },
            { cle: "l", titre: "Libellé", rendu: (l) => l.libelle },
            { cle: "m", titre: "Montant", montant: true, rendu: (l) => fcfa(l.montant) },
          ]}
          lignes={introuvables}
          cle={(l) => l.id}
          vide="Aucun justificatif manquant au stockage."
        />
      </section>

      <section>
        <h2>Pièces déposées qu'aucune écriture ne réclame</h2>
        <Tableau
          colonnes={[
            { cle: "c", titre: "Emplacement", rendu: (d) => d.cheminLogique },
            {
              cle: "s",
              titre: "Taille",
              montant: true,
              rendu: (d) => `${Math.round(d.taille / 1024)} Ko`,
            },
            { cle: "dep", titre: "Déposé le", rendu: (d) => d.deposeLe.slice(0, 10) },
          ]}
          lignes={orphelins}
          cle={(d) => d.id}
          vide="Toutes les pièces déposées sont rattachées."
        />
      </section>
    </div>
  );
}

// ===========================================================================
// TRANSMISSIONS
// ===========================================================================

const TransmissionsSchema = z.array(
  z.object({
    id: z.string(),
    annee: z.number(),
    mois: z.number(),
    scelleeLe: z.string(),
    scelleePar: z.string(),
    empreinteManifeste: z.string(),
    nbReeditions: z.number(),
    nbPieces: z.number(),
  }),
);

export function Transmissions({ dossierId }: EcranProps) {
  const q = useApi(
    ["transmissions", dossierId],
    `/dossiers/${dossierId}/transmissions`,
    TransmissionsSchema,
  );

  if (q.isLoading) return <EtatChargement quoi="des transmissions" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  return (
    <div>
      <p className="resume">
        Une transmission scelle un instantané : empreinte SHA-256 de chaque pièce,
        copie archivée immuable, procès-verbal à la charte. Les pièces couvertes
        deviennent <strong>indestructibles</strong>.
      </p>

      <Tableau
        colonnes={[
          { cle: "p", titre: "Période", rendu: (t) => `${String(t.mois).padStart(2, "0")}/${t.annee}` },
          { cle: "d", titre: "Scellée le", rendu: (t) => t.scelleeLe.slice(0, 19).replace("T", " ") },
          { cle: "par", titre: "Par", rendu: (t) => t.scelleePar },
          { cle: "n", titre: "Pièces", montant: true, rendu: (t) => t.nbPieces },
          {
            cle: "e",
            titre: "Empreinte du manifeste",
            rendu: (t) => <code title={t.empreinteManifeste}>{t.empreinteManifeste.slice(0, 16)}</code>,
          },
          { cle: "r", titre: "Rééditions", montant: true, rendu: (t) => t.nbReeditions },
          {
            cle: "pv",
            titre: "Procès-verbal",
            rendu: (t) => (
              <>
                <a href={`/api/dossiers/${dossierId}/transmissions/${t.id}/pv`}>Original</a>
                {" · "}
                <a href={`/api/dossiers/${dossierId}/transmissions/${t.id}/pv?reedition=true`}>
                  Rééditer
                </a>
              </>
            ),
          },
        ]}
        lignes={q.data ?? []}
        cle={(t) => t.id}
        vide="Aucune transmission scellée."
      />

      <p className="note">
        Une réédition reprend le manifeste scellé — mêmes pièces, mêmes empreintes,
        même date — et porte la mention « Réédition ». Les anciens procès-verbaux
        gardent leur apparence d'origine : on ne réécrit pas l'histoire en silence.
      </p>
    </div>
  );
}

export { DocumentSchema };
